import { FormGroup } from '@angular/forms';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';
import { ProductSelectionValue, createProductSelectionFormGroup } from './product-selection/product-selection.model';
import { createTimerFormGroup, TimerFormShape } from '../../components/timer-config/timer.model';

/** Variables exposed to the Upsell Items custom-code template (recommended products + appearance). */
export const UPSELL_ITEMS_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  {
    reference: '{{sectionTitle}}',
    type: 'String',
    description: 'Rich-text HTML of the section title — render with triple braces to preserve formatting',
  },
  {
    reference: '{{sectionTitleColor}}',
    type: 'String',
    description: "Hex of the section title's colour; empty means inherit from the theme",
  },
  { reference: '{{products}}', type: 'Array', description: 'The recommended products resolved for this cart' },
  { reference: '{{products[i].id}}', type: 'String', description: 'Shopify product ID' },
  { reference: '{{products[i].title}}', type: 'String', description: 'Product title' },
  { reference: '{{products[i].price}}', type: 'String', description: 'Formatted price in the shop currency' },
  {
    reference: '{{products[i].compareAtPrice}}',
    type: 'String',
    description:
      'Formatted struck-through price: the pre-discount price when the group discounts, else the Shopify compare-at when higher; empty otherwise',
  },
  {
    reference: '{{products[i].imageUrl}}',
    type: 'String',
    description: 'Product image URL (empty when the product has no image)',
  },
  { reference: '{{products[i].vendor}}', type: 'String', description: 'Product vendor' },
  { reference: '{{products[i].productType}}', type: 'String', description: 'Product type' },
  {
    reference: '{{products[i].defaultVariantId}}',
    type: 'String',
    description: 'First / default variant ID, for the add-to-order action',
  },
  {
    reference: 'data-pdq-action="add-upsell"',
    type: 'Action',
    description: 'Put on your add button, with data-variant-id, to add that product to the cart. Adding is ours.',
  },
  { reference: '{{button.text}}', type: 'String', description: "The admin's add-to-order button label" },
  // already resolved widget ?? global by the runtime, so an inherited button still gets a colour
  {
    reference: '{{button.backgroundColor}}',
    type: 'String',
    description: 'Button background hex — the widget override, or the global Button Design value when inherited',
  },
  {
    reference: '{{button.textColor}}',
    type: 'String',
    description: 'Button text hex — the widget override, or the global Button Design value when inherited',
  },
  {
    reference: '{{button.hoverColor}}',
    type: 'String',
    description: 'Button hover hex — the widget override, or the global Button Design value when inherited',
  },
  {
    reference: '{{button.iconColor}}',
    type: 'String',
    description: 'Button icon hex — the widget override, or the global Button Design value when inherited',
  },
  {
    reference: '{{button.cornerRadius}}',
    type: 'Number',
    description: 'Button corner radius (px) — the widget override, or the global Button Design value when inherited',
  },
];

export const UPSELL_ITEMS_STARTER_TEMPLATE = `<div>
  <div class="upsell-items__title">{{{sectionTitle}}}</div>

  <div class="upsell-items__list">
    {{#each products}}
      <div class="upsell-item" data-product-id="{{id}}">
        {{#if imageUrl}}
          <img class="upsell-item__image" src="{{imageUrl}}" alt="{{title}}" />
        {{/if}}
        <div class="upsell-item__title">{{title}}</div>
        <div class="upsell-item__price">{{price}}{{#if compareAtPrice}} <s>{{compareAtPrice}}</s>{{/if}}</div>
        <button
          class="upsell-item__button"
          data-pdq-action="add-upsell"
          data-variant-id="{{defaultVariantId}}"
          style="background-color: {{../button.backgroundColor}}; color: {{../button.textColor}}; border-radius: {{../button.cornerRadius}}px;"
        >
          {{../button.text}}
        </button>
      </div>
    {{/each}}
  </div>
</div>`;

/**
 * Upsell Items widget settings (widget-level contract).
 *
 * One `settings` JSON per widget instance (persisted on `smart_cart_widgets.settings`), NAMESPACED
 * by tab so the two developers building this widget never collide on the shared FormGroup:
 *   - `settings` / `productSelection` / `timer`  → this widget's tabs
 *   - `goals` / `exitIntent`                     → the other developer (add their groups below)
 *
 * `createUpsellItemsFormGroup().getRawValue()` is exactly the settings payload the backend stores
 * and the storefront runtime reads. Each tab's form model lives with its component — e.g. the
 * Product Selection model in `product-selection/product-selection.model.ts`.
 */
export interface SettingsTabShape {
  customCode: FormGroup<CustomCodeFormShape>;
}

export interface UpsellItemsFormShape {
  /** Settings tab — Custom code only for v1 (discounts deferred to a future ticket). */
  settings: FormGroup<SettingsTabShape>;
  /** Product Selection tab — rule groups + restrictions + appearance (Phase 1). */
  productSelection: FormGroup;
  /** Timer tab — countdown, via the shared timer-config component/model (reused from Goals). */
  timer: FormGroup<TimerFormShape>;
  // The Goals and Exit-intent tabs are owned by another dev and add their own groups to this shape.
}

export function createUpsellItemsFormGroup(
  initialSettings?: Record<string, unknown> | null,
  rulesDynamicFormService?: RulesDynamicFormService,
): FormGroup<UpsellItemsFormShape> {
  const initial = (initialSettings ?? {}) as Record<string, unknown>;

  const formGroup = new FormGroup<UpsellItemsFormShape>({
    settings: new FormGroup<SettingsTabShape>({
      customCode: createCustomCodeFormGroup(undefined, UPSELL_ITEMS_STARTER_TEMPLATE),
    }),
    productSelection: createProductSelectionFormGroup(
      initial['productSelection'] as ProductSelectionValue | undefined,
      rulesDynamicFormService,
    ),
    timer: createTimerFormGroup(initial['timer'] as Record<string, unknown> | undefined),
  });

  // productSelection is built from initial above (FormArrays can't be created by patchValue);
  // patch only the flat sub-trees.
  const settingsInit = initial['settings'] as { customCode?: Partial<{ enabled: boolean; code: string }> } | undefined;
  if (settingsInit?.customCode) {
    formGroup.controls.settings.controls.customCode.patchValue(settingsInit.customCode, { emitEvent: false });
  }

  return formGroup;
}
